import { Check, ChevronLeft, ChevronRight, Copy, X } from 'lucide-react'
import { useEffect, useState } from 'react'

/** Paging between documents that share one block, as the examples do. */
export interface BlockPager {
  index: number
  count: number
  label: string
  onStep: (by: number) => void
}

/**
 * One YAML document, with a copy control in the block's own header.
 *
 * It began as a two-format block, YAML and JSON, chosen in that header the way every
 * developer-facing reference does it. The second format is gone: the Question Schema is YAML and
 * only YAML, so there is nothing to choose between and the header carries the copy control alone.
 *
 * Three states on that control, not two. `navigator.clipboard.writeText` REJECTS when the browser
 * refuses -- a document that is not focused, a permission withheld, anything not on a secure
 * origin -- so a fire-and-forget write leaves the control claiming success over nothing. The
 * failed state has to say what to do instead, because the whole point of the screen is getting
 * this text out of it.
 */
export function SchemaBlock({
  yaml,
  pager,
  className = '',
}: {
  yaml: string
  pager?: BlockPager
  className?: string
}) {
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle')

  useEffect(() => {
    if (state === 'idle') return
    const timer = setTimeout(() => setState('idle'), 3000)
    return () => clearTimeout(timer)
  }, [state])

  // Paging away with "Copied" showing would leave the message describing the document you just
  // left.
  const page = pager?.index
  useEffect(() => {
    setState('idle')
  }, [page])

  // The icon is the only thing that changes once the word is gone, and an icon has no accessible
  // name of its own -- so the name carries the state.
  const Icon = state === 'copied' ? Check : state === 'failed' ? X : Copy
  const label = state === 'copied' ? 'Copied' : state === 'failed' ? 'Copy failed' : 'Copy'

  return (
    <div className={`overflow-hidden rounded-lg border border-ink-100 bg-card ${className}`}>
      <div className="flex items-center justify-between gap-4 border-b border-ink-100 bg-sand-050 px-3 py-2">
        <span className="px-1 text-sm font-bold tracking-wide text-muted uppercase">yaml</span>

        {pager && (
          <div className="flex min-w-0 items-center gap-1">
            <button
              type="button"
              aria-label="Previous example"
              title="Previous example"
              onClick={() => pager.onStep(-1)}
              className="flex rounded-md p-1 text-green-800 hover:bg-green-050"
            >
              <ChevronLeft aria-hidden="true" className="size-4" />
            </button>
            {/* The count and the title in one live region: paging changes both, and announcing
                them separately would read as two unrelated updates. */}
            <span aria-live="polite" className="truncate text-sm text-muted">
              {pager.index + 1} of {pager.count} · {pager.label}
            </span>
            <button
              type="button"
              aria-label="Next example"
              title="Next example"
              onClick={() => pager.onStep(1)}
              className="flex rounded-md p-1 text-green-800 hover:bg-green-050"
            >
              <ChevronRight aria-hidden="true" className="size-4" />
            </button>
          </div>
        )}

        {/* An icon button, named by aria-label and titled the same way the cog is (Menu.tsx).
            The title matters more here than on the cog: a failed copy shows only a red cross,
            and a sighted user needs somewhere to read what went wrong. */}
        <button
          type="button"
          aria-label={label}
          title={label}
          onClick={() => {
            navigator.clipboard.writeText(yaml).then(
              () => setState('copied'),
              () => setState('failed'),
            )
          }}
          className={
            'flex rounded-md p-1.5 hover:bg-green-050 ' +
            (state === 'failed' ? 'text-red-700' : 'text-green-800')
          }
        >
          <Icon aria-hidden="true" className="size-4" />
        </button>
      </div>

      {/* The control's label changes, which a screen reader following focus will not announce on
          its own. Said once, here, rather than as an alert. */}
      <p className="sr-only" aria-live="polite">
        {state === 'copied' ? 'The schema is on the clipboard.' : ''}
        {state === 'failed'
          ? 'The browser refused the clipboard. Select the schema below and copy it.'
          : ''}
      </p>

      <pre className="overflow-x-auto px-4 py-4 text-[13px] leading-relaxed text-ink-700">
        <code>{yaml}</code>
      </pre>
    </div>
  )
}
